import * as React from 'react';
import { ChevronDown, ChevronRight, type LucideIcon } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Box, type BoxProps } from '../../components/ui/box';
import { Icon } from '../../components/ui/icon';
import { Text, type TextProps } from '../../components/ui/text';
import { useDataTableDensity } from './data-table-context';
import type { DataTableDensity } from './data-table-types';
import type { Omittable } from "../../lib/props";

// Density → Text size prop (mirrors DENSITY_CONFIG.textSize; the header text
// takes the size through Text's own variant so the responsive default pair
// never fights it).
const DENSITY_TEXT_SIZE: Record<DataTableDensity, TextProps['size']> = {
  compact: 'xs',
  regular: 'sm',
  spacious: 'base',
};

export type DataTableGroupHeaderProps = Omit<BoxProps, 'title'> &
  Omittable<{
    title: React.ReactNode;
    count?: number | string;
    icon?: LucideIcon;
    /** Group navigator cluster, rendered immediately right of the label/count. */
    nav?: React.ReactNode;
    actions?: React.ReactNode;
    showChevron?: boolean;
    /** When true, hides the chevron and prevents expand/collapse interaction. */
    disabled?: boolean;
    /** Controlled expanded state. */
    isExpanded?: boolean;
    /** Toggle callback. */
    onToggle?: () => void;
  }>;

type DataTableGroupHeaderLabelProps = Omittable<{
  title: React.ReactNode;
  icon?: LucideIcon;
  showChevron: boolean;
  ChevronIcon: LucideIcon;
  densityTextSize: TextProps['size'];
}>;

function DataTableGroupHeaderLabel({
  title,
  icon,
  showChevron,
  ChevronIcon,
  densityTextSize,
}: DataTableGroupHeaderLabelProps) {
  return (
    <>
      {showChevron && <Icon as={ChevronIcon} size="md" color="muted" />}
      {icon ? <Icon as={icon} size="lg" color="muted" /> : null}
      <Box minWidth={0} className="shrink">
        {React.isValidElement(title) ? (
          title
        ) : (
          <Text weight="medium" truncate size={densityTextSize}>
            {title}
          </Text>
        )}
      </Box>
    </>
  );
}

type DataTableGroupHeaderBodyProps = Omittable<{
  control: React.ReactNode;
  count?: number | string;
  nav?: React.ReactNode;
  actions?: React.ReactNode;
  densityTextSize: TextProps['size'];
}>;

function DataTableGroupHeaderBody({
  control,
  count,
  nav,
  actions,
  densityTextSize,
}: DataTableGroupHeaderBodyProps) {
  return (
    <>
      <Box minWidth={0} maxWidth="100%"
        direction="row"
        align="center"
        gap="sm"
        className="shrink"
      >
        {control}
        {count !== undefined ? (
          <Text
            variant="muted"
            truncate
            size={densityTextSize}
            className="shrink-0"
          >
            {count}
          </Text>
        ) : null}
      </Box>
      {nav ?? null}
      {actions ? (
        <Box direction="row" align="center" gap="xs" shrink={false}>
          {actions}
        </Box>
      ) : null}
    </>
  );
}

/**
 * Sticky group header — chevron toggle, title, count, navigator cluster and
 * actions. Expanded state is fully controlled (`isExpanded` + `onToggle`);
 * the core owns group open/close state.
 */
export function DataTableGroupHeader({
  title,
  count,
  icon,
  nav,
  actions,
  showChevron = true,
  disabled = false,
  isExpanded,
  onToggle,
  className,
  ...props
}: DataTableGroupHeaderProps) {
  const density = useDataTableDensity();
  const densityTextSize = DENSITY_TEXT_SIZE[density];
  const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;

  return (
    <Box
      direction="row"
      align="center"
      className={cn('gap-3', className)}
      {...props}
    >
      <DataTableGroupHeaderBody
        count={count}
        nav={nav}
        actions={actions}
        densityTextSize={densityTextSize}
        control={
          <Box minWidth={0}
            as="button"
            direction="row"
            align="center"
            gap="sm"
            paddingY="sm"
            onClick={disabled ? undefined : onToggle}
            aria-expanded={!!isExpanded}
            aria-disabled={disabled || undefined}
            className={cn(
              'shrink',
              disabled ? 'cursor-default' : 'cursor-pointer'
            )}
          >
            <DataTableGroupHeaderLabel
              title={title}
              icon={icon}
              showChevron={showChevron}
              ChevronIcon={ChevronIcon}
              densityTextSize={densityTextSize}
            />
          </Box>
        }
      />
    </Box>
  );
}
